import { useCallback, useEffect, useRef, useState } from "react";

import { apiClient, type MentionSummary, type OverviewAttention } from "@/shared/api/api-client";
import {
  COUNTED_LINES,
  DEFAULT_OVERVIEW_ATTENTION,
  attentionWindow,
  completeAttention,
  countQuery,
  lineApplies,
  lineCriteria,
  type AttentionApplicability,
  type AttentionCounts
} from "./attention-lines";

/**
 * O ajuste do painel "Pontos de atenção", carregado UMA vez ao abrir (sem
 * polling: só muda quando alguém salva).
 *
 * **Falha de carga não aparece**: o painel fica no default, que é o painel
 * de sempre. Um aviso de erro ali seria mais barulho do que a falta do
 * ajuste. A falha de SALVAR, essa sim, volta pra quem clicou (`save`
 * rejeita).
 *
 * O descarte é por carga (contador em `useRef`), como na lista de
 * configuração: um salvamento que termina antes da leitura inicial não é
 * sobrescrito pela resposta velha.
 */
export function useOverviewAttention(token: string, enabled = true) {
  const [attention, setAttention] = useState<OverviewAttention>(DEFAULT_OVERVIEW_ATTENTION);
  const loadId = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const mine = ++loadId.current;
    apiClient.overviewAttention(token).then(
      result => {
        if (mine === loadId.current && result && typeof result === "object") {
          setAttention(completeAttention(result));
        }
      },
      () => undefined
    );
  }, [enabled, token]);

  const save = useCallback(async (next: OverviewAttention) => {
    const mine = ++loadId.current;
    const saved = await apiClient.updateOverviewAttention(token, next);
    if (mine === loadId.current) setAttention(completeAttention(saved));
  }, [token]);

  return { attention, save };
}

/**
 * Os números das linhas que não vêm prontos no resumo (2026-11-02): uma
 * contagem do recorte de cada linha LIGADA (`GET /mentions/count`, que tem
 * cache) e a lista de rascunhos de amostra pendentes.
 *
 * Só conta o que está ligado e faz sentido pra empresa — com o default, as
 * linhas contadas estão todas desligadas e nada sai daqui. Conta de novo
 * quando muda o ajuste, a janela do resumo ou o `nonce` (o "Atualizar
 * dados" da tela); o polling do upload ativo recarrega o resumo, mas com a
 * mesma janela não dispara nova contagem.
 */
export function useAttentionCounts({
  token,
  attention,
  summary,
  summaryLoading,
  applicability,
  nonce
}: {
  token: string;
  attention: OverviewAttention;
  summary: MentionSummary | null;
  /** Enquanto o resumo carrega, a falta de janela é espera, não falha. */
  summaryLoading: boolean;
  applicability: AttentionApplicability;
  nonce: number;
}) {
  const [counts, setCounts] = useState<AttentionCounts>({});
  const [draftExpiries, setDraftExpiries] = useState<string[] | null | undefined>(undefined);
  const loadId = useRef(0);

  const window = attentionWindow(summary);
  const wanted = COUNTED_LINES.filter(
    key => attention[key].enabled && lineApplies(key, applicability)
  );
  const wantsDrafts =
    attention.expiring_drafts.enabled && lineApplies("expiring_drafts", applicability);
  // A chave do efeito é TEXTO: o resumo é um objeto novo a cada tick do
  // polling, e o que importa é se a janela ou o pedido mudaram.
  const key = JSON.stringify([wanted, wantsDrafts, window, nonce, summaryLoading]);

  useEffect(() => {
    const mine = ++loadId.current;
    // Sem resumo (a chamada falhou) não há janela pra contar: a linha diz
    // que não contou, em vez de ficar em "Contando…" pra sempre.
    setCounts(window || summaryLoading ? {} : Object.fromEntries(wanted.map(line => [line, null])));
    setDraftExpiries(undefined);
    if (window) {
      for (const line of wanted) {
        const criteria = lineCriteria(line, window);
        if (!criteria) continue;
        apiClient.countMentions(token, countQuery(criteria)).then(
          result => {
            if (mine === loadId.current) setCounts(current => ({ ...current, [line]: result.total }));
          },
          () => {
            if (mine === loadId.current) setCounts(current => ({ ...current, [line]: null }));
          }
        );
      }
    }
    if (wantsDrafts) {
      apiClient.listSocialReportDrafts(token).then(
        drafts => {
          if (mine === loadId.current) setDraftExpiries(drafts.map(draft => draft.expires_at));
        },
        () => {
          if (mine === loadId.current) setDraftExpiries(null);
        }
      );
    }
    // `key` resume `wanted`, `wantsDrafts`, `window`, `nonce` e a carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, token]);

  return { counts, draftExpiries };
}
